import { REGEXP_ONLY_DIGITS } from 'input-otp';
import type * as React from 'react';

import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/lumen/input-otp';
import { useLanguage } from '@contexts/LanguageContext';

const SLOTS = [0, 1, 2, 3, 4, 5] as const;

/** Spaces and dashes an authenticator app or a password manager copies along with the code. */
const stripSeparators = (pasted: string) => pasted.replace(/[\s-]/g, '');

/**
 * The six-digit authenticator code: digits only, paste fills every box, the one-time-code
 * autofill hint is set, and one labelled <input> carries it for screen readers.
 * Replaces Mantine's `PinInput length={6} type="number" oneTimeCode`.
 */
export function MFACodeInput({
  value,
  onChange,
  disabled,
  invalid,
  describedBy,
  ref,
  autoFocus,
  size = 'lg',
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  ref?: React.Ref<HTMLInputElement>;
  autoFocus?: boolean;
  size?: 'lg' | 'xl';
}) {
  const { t } = useLanguage();
  const slot = size === 'xl' ? 'size-12 text-xl' : 'size-11 text-lg';

  return (
    <InputOTP
      ref={ref}
      autoFocus={autoFocus}
      maxLength={6}
      pattern={REGEXP_ONLY_DIGITS}
      inputMode="numeric"
      autoComplete="one-time-code"
      pasteTransformer={stripSeparators}
      value={value}
      onChange={onChange}
      disabled={disabled}
      aria-label={t('lumen.auth.otpLabel')}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      containerClassName="justify-center"
    >
      <InputOTPGroup>
        {SLOTS.map((index) => (
          <InputOTPSlot key={index} index={index} className={slot} aria-invalid={invalid || undefined} />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
}
